import React, { useCallback } from "react";
import { SCHEDULE_POST_MODAL } from "../../../constants/modal.constant";
import { useGlobalCore } from "../../../cores/globals";
import { SocialPost } from "../../../cores/socialPost/interfaces/api/get-social-posts";
import { IconMapper } from "../socialPost/selector/SocialPostSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { getSocialPostCalendarMoment } from "./socialPostCalendarMoment";

/** Evita que el clic en el ojo dispare también el handler de la tarjeta/celda. */
function stopOpeningComposer(e: React.SyntheticEvent) {
  e.stopPropagation();
}

interface Props {
  post: SocialPost;
  onOpenPostComposer?: (post: SocialPost) => void;
  draggable?: boolean;
}

export const PostItem = (props: Props) => {
  const { post, onOpenPostComposer, draggable = false } = props;
  const { turnOnModal } = useGlobalCore();

  const openComposerModal = useCallback(() => {
    if (onOpenPostComposer) {
      onOpenPostComposer(post);
      return;
    }
    turnOnModal(SCHEDULE_POST_MODAL, true);
  }, [onOpenPostComposer, post, turnOnModal]);

  const renderIconByProvider = useCallback((providerId: string, size: "1x" | "2x" = "2x") => {
    const normalized = (providerId || "").toLowerCase();
    switch (normalized) {
      case "facebook":
        return IconMapper.facebook?.({ active: true, size });
      case "twitter":
        return IconMapper.twitter?.({ active: true, size });
      case "linkedin":
        return IconMapper.linkedIn?.({ active: true, size });
      case "linkedinbusiness":
        return IconMapper.linkedInBusiness?.({ active: true, size });
      case "instagram":
        return IconMapper.instagram?.({ active: true, size });
      case "tiktok":
        return IconMapper.tiktok?.({ active: true, size });
      default:
        return null;
    }
  }, []);

  const providerIds = Array.from(
    new Set(
      [
        post.post.socialProvider,
        ...(post.mention || [])
          .flatMap((m) => (m.mentions || []).map((x) => x.socialProviderId))
          .filter(Boolean),
      ]
        .map((id) => (id || "").toLowerCase())
        .filter(Boolean)
    )
  );

  const postDate = getSocialPostCalendarMoment(post);
  const shortText = (post.post.text || "").trim();
  const previewText =
    shortText.length > 120 ? `${shortText.slice(0, 120)}...` : shortText;
  const mediaUrl = post.media?.[0]?.url;
  const statusLabel =
    post.post.status === 0 ? "Pendiente" : post.post.status === 1 ? "Publicado" : "Programado";

  return (
    <div
      className="social-calendar-post-item pointer"
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.stopPropagation();
        e.dataTransfer.setData("text/plain", post.post.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={(e) => {
        e.stopPropagation();
        openComposerModal();
      }}
      role="button"
    >
      <div className="social-calendar-post-item__compact">
        <div className="social-calendar-post-item__compact-top">
          <div className="social-calendar-post-item__icon-stack">
            {providerIds.slice(0, 3).map((id) => (
              <span className="social-calendar-post-item__icon" key={`compact-${id}`}>
                {renderIconByProvider(id, "2x")}
              </span>
            ))}
          </div>
          <span className="social-calendar-post-item__time">
            {postDate.isValid() ? postDate.format("HH:mm") : "--:--"}
          </span>
        </div>
        <p className="social-calendar-post-item__compact-text">{previewText || "Post programado"}</p>
        <div className="social-calendar-post-item__compact-bottom">
          {mediaUrl ? (
            <img
              className="social-calendar-post-item__thumb"
              src={mediaUrl}
              alt="media"
              loading="lazy"
            />
          ) : (
            <span />
          )}
          <button
            type="button"
            className="social-calendar-post-item__external-btn"
            title="Abrir detalle"
            aria-label="Abrir detalle del post"
            onPointerDown={stopOpeningComposer}
            onMouseDown={stopOpeningComposer}
            onClick={(e) => {
              stopOpeningComposer(e);
              openComposerModal();
            }}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        </div>
      </div>

      <div className="social-calendar-post-item__popover">
        <div className="social-calendar-post-item__head">
          <div className="social-calendar-post-item__icon-row">
            {providerIds.slice(0, 4).map((id) => (
              <span className="social-calendar-post-item__icon" key={`popover-${id}`}>
                {renderIconByProvider(id, "2x")}
              </span>
            ))}
          </div>
          <span className="social-calendar-post-item__date">
            {postDate.isValid() ? postDate.format("DD MMM YYYY HH:mm") : "--"}
          </span>
        </div>

        <div className="social-calendar-post-item__status-row">
          <span className="social-calendar-post-item__status-dot" />
          <span className="social-calendar-post-item__status-text">{statusLabel}</span>
          <button
            type="button"
            className="social-calendar-post-item__external-btn"
            title="Abrir detalle"
            aria-label="Abrir detalle del post"
            onPointerDown={stopOpeningComposer}
            onMouseDown={stopOpeningComposer}
            onClick={(e) => {
              stopOpeningComposer(e);
              openComposerModal();
            }}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        </div>

        <div className="social-calendar-post-item__divider" />

        <p className="social-calendar-post-item__text">{previewText || "Post programado"}</p>

        {mediaUrl ? (
          <img
            className="social-calendar-post-item__image"
            src={mediaUrl}
            alt="media"
            loading="lazy"
          />
        ) : null}
      </div>
    </div>
  );
};
